import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { LoginModal } from "../auth/components/LoginModal";
import { RegisterModal } from "../auth/components/RegisterModal";
import { CookieSettingsButton } from "../../components/gdpr";
import { Logo } from "../../components/common/Logo";
import { LandingNav } from "./components/LandingNav";
import { PlansGrid } from "../../components/pricing/PlansGrid";

// Where merchants are sent when website self-serve billing is disabled and
// plans are sold via Shopify (App Store listing handle = "dashbal").
const SHOPIFY_APP_STORE_URL = "https://apps.shopify.com/dashbal";

const PricingPage = () => {
    const { t } = useTranslation("common");
    const [searchParams, setSearchParams] = useSearchParams();
    // True when the website returns no self-serve plans (billing handled via
    // Shopify). Drives a graceful notice instead of the empty "no plan" state.
    const [billingDisabled, setBillingDisabled] = useState(false);
    const handlePlansResolved = useCallback((hasPlans: boolean) => {
        setBillingDisabled(!hasPlans);
    }, []);

    const activeModal = searchParams.get("modal");
    const openLogin = () => setSearchParams({ modal: "login" });
    const openRegister = () => setSearchParams({ modal: "register" });
    const closeModal = () => setSearchParams({});

    return (
        <div className="min-h-screen flex flex-col bg-background-page font-sans selection:bg-accent-primary/20 selection:text-text-primary">
            <LoginModal isOpen={activeModal === "login"} onClose={closeModal} />
            <RegisterModal
                isOpen={activeModal === "register"}
                onClose={closeModal}
                prefillEmail={searchParams.get("email") || undefined}
            />

            <LandingNav onLogin={openLogin} onRegister={openRegister} />

            <main className="flex-1 pb-20">
                {/* Hero */}
                <section className="relative overflow-hidden">
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0 opacity-[0.3] dark:opacity-[0.2]"
                        style={{
                            backgroundImage:
                                "linear-gradient(to right, rgba(120,120,120,0.12) 1px, transparent 1px), linear-gradient(to bottom, rgba(120,120,120,0.12) 1px, transparent 1px)",
                            backgroundSize: "56px 56px",
                            maskImage:
                                "radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 75%)",
                            WebkitMaskImage:
                                "radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 75%)",
                        }}
                    />
                    <div className="relative container mx-auto px-4 sm:px-6 text-center max-w-3xl pt-24 sm:pt-28 pb-12 sm:pb-16">
                        <motion.p
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                            className="text-xs font-semibold tracking-[0.2em] uppercase text-accent-primary mb-3"
                        >
                            {t("landing.pricing.eyebrow")}
                        </motion.p>
                        {!billingDisabled && (
                            <>
                                <motion.h1
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.05 }}
                                    className="text-4xl sm:text-5xl font-bold font-display text-text-primary tracking-tight mb-4"
                                >
                                    {t("landing.pricing.title")}
                                </motion.h1>
                                <motion.p
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.12 }}
                                    className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400"
                                >
                                    {t("landing.pricing.subtitle")}
                                </motion.p>
                            </>
                        )}
                    </div>
                </section>

                {/* Plans */}
                <section className="container mx-auto">
                    <PlansGrid
                        subscription={null}
                        onSelectPlan={openRegister}
                        publicCtaLabel={String(t("landing.pricing.publicCta"))}
                        appStoreUrl={SHOPIFY_APP_STORE_URL}
                        onPlansResolved={handlePlansResolved}
                    />
                </section>

                {/* Footnote */}
                {!billingDisabled && (
                    <p className="text-center text-sm text-neutral-500 dark:text-neutral-400 mt-4 px-4">
                        {t("landing.pricing.footnote")}
                    </p>
                )}

                {/* Future sections will go below */}
            </main>

            {/* Footer */}
            <footer className="bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800 py-10">
                <div className="container mx-auto px-4 sm:px-6 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-neutral-500 dark:text-neutral-400">
                    <div className="flex items-center gap-2">
                        <Logo className="w-5 h-5" />
                        <span className="text-lg font-medium font-display text-text-primary dark:text-neutral-100 tracking-tight">
                            {t("brandName")}
                        </span>
                    </div>
                    <div className="flex flex-wrap justify-center gap-5">
                        <a href="/terms" className="hover:text-text-primary transition-colors">
                            {t("legal.tos.title")}
                        </a>
                        <a href="/privacy" className="hover:text-text-primary transition-colors">
                            {t("legal.privacy.title")}
                        </a>
                        <CookieSettingsButton variant="text" />
                    </div>
                    <div>{t("landing.copyright")}</div>
                </div>
            </footer>
        </div>
    );
};

export default PricingPage;
